import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import FrameModeDropdown from 'components/Widget/FrameModeDropdown';
import { Frame } from 'dragonmouse-shared/interfaces';
import { GetSiteDesignSystemInfoResponse } from 'dragonmouse-shared/api/site';
import useOnClickOutside from 'hooks/useOnClickOutside';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import * as S from './styles';

interface DsControlsProps {
	frame: Frame;
}

const DsControls = ({ frame }: DsControlsProps) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const [dsInfo, setDsInfo] = useState<GetSiteDesignSystemInfoResponse | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------
	useEffect(() => {
		// Clear popover + dsInfo immediately on siteId change so the previous
		// site's info can't flash (or stay popped open) while the new fetch is in flight.
		setDsInfo(null);
		setIsOpen(false);
		if (!frame.siteId) return;
		let cancelled = false;
		forge
			.getSiteDesignSystemInfo({ siteId: frame.siteId })
			.then((data: GetSiteDesignSystemInfoResponse) => {
				if (!cancelled) setDsInfo(data);
			})
			.catch((error: any) => {
				console.error('Failed to fetch site DS info:', error);
				Sentry.captureException(error);
				if (!cancelled) setDsInfo(null);
			});
		return () => {
			cancelled = true;
		};
	}, [frame.siteId]);

	useOnClickOutside(containerRef, () => {
		if (isOpen) setIsOpen(false);
	});

	useEffect(() => {
		if (!isOpen) return;
		const onEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setIsOpen(false);
		};
		window.addEventListener('keydown', onEscape);
		return () => window.removeEventListener('keydown', onEscape);
	}, [isOpen]);

	if (!dsInfo?.designSystemId) return null;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onHeaderClick = () => {
		if (dsInfo.designSystemId && dsInfo.designSystemTitle) {
			setIsOpen(false);
			navigate(routes.designSystem(dsInfo.designSystemId, dsInfo.designSystemTitle));
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	return (
		<S.Base ref={containerRef}>
			<S.IconButton
				type="button"
				aria-label={dsInfo.designSystemTitle ?? 'Design System'}
				onClick={() => setIsOpen((v) => !v)}
				data-tooltip-content={dsInfo.designSystemTitle ?? 'Design System'}
			>
				<Icon type={IconType.STACK_LINE} width={18} height={18} color={theme.gray[1000]} />
			</S.IconButton>
			<S.Popover $isOpen={isOpen}>
				<S.Label>Design System</S.Label>
				<S.Header $clickable onClick={onHeaderClick}>
					<S.NameGroup>
						<S.Name>{dsInfo.designSystemTitle}</S.Name>
						{dsInfo.versionNumber != null && <S.VersionText>v{dsInfo.versionNumber}</S.VersionText>}
					</S.NameGroup>
					<S.HeaderArrow>
						<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} color={theme.gray[400]} />
					</S.HeaderArrow>
				</S.Header>
			</S.Popover>
			<FrameModeDropdown frame={frame} dsModes={dsInfo.dsModes} compact />
		</S.Base>
	);
};

export default DsControls;
